import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, Copy, Check, Rocket, AlertTriangle, Globe, FileText, RotateCcw, ArrowUpCircle, List, ShieldCheck, ShieldAlert, XOctagon, Search } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import vercelIcon from '../assets/icons/vercel.svg';
import vercelLightIcon from '../assets/icons/vercel-light.svg';

const VercelIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const vcIcon = isDark ? vercelLightIcon : vercelIcon;
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connectedTeamId, setConnectedTeamId] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [exampleBranch, setExampleBranch] = useState('');
  const [exampleDeveloper, setExampleDeveloper] = useState('');
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);

  const [teamId, setTeamId] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookSecretConfigured, setWebhookSecretConfigured] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getVercelStatus();
      if (data?.connected) {
        setConnected(true);
        setConnectedTeamId(data.team_id || '');
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
        setWebhookSecretConfigured(data.webhook_secret_configured || false);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectVercel({
        api_token: apiToken,
        team_id: teamId || undefined,
      });
      setSuccessMsg('Vercel connected successfully!');
      setApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectVercel();
      setConnected(false);
      setConnectedTeamId('');
      setSuccessMsg('Vercel disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testVercel();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Vercel (${data.project_count ?? 0} project${data.project_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveWebhookSecret = async () => {
    setError(null);
    setSavingSecret(true);
    try {
      await apiClient.saveVercelWebhookSecret({ webhook_secret: webhookSecret });
      setWebhookSecret('');
      setWebhookSecretConfigured(true);
      setSuccessMsg('Webhook secret saved. Signature verification is now active.');
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to save webhook secret');
    } finally {
      setSavingSecret(false);
    }
  };

  const webhookUrl = `${window.location.origin}/api/webhooks/vercel`;

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}

      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={vcIcon} alt="Vercel" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Vercel
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Deployment monitoring, build log analysis, and rollback automation</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {connectedTeamId && (
                <div>
                  <span className="text-muted-foreground">Team ID</span>
                  <p className="font-medium">{connectedTeamId}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Webhook Events</span>
                <p className="font-medium">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''}</p>
              </div>
              {lastWebhookAt && (
                <div>
                  <span className="text-muted-foreground">Last Webhook</span>
                  <p className="font-medium">{new Date(lastWebhookAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Webhook Setup
              {lastWebhookAt ? (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''} received</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground text-xs font-normal">No events yet</Badge>
              )}
            </CardTitle>
            <CardDescription>To use Vercel triggers (Deployment Failed, Error Anomaly), configure Vercel to send events to Kestrel.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Webhook URL</p>
              <div className="flex items-center gap-2">
                <code className="bg-background px-2.5 py-1.5 rounded text-sm border flex-1 truncate select-all">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}>
                  {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>

            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                <span>Go to your Vercel dashboard → <strong className="text-foreground">Settings → Webhooks</strong> (or <strong className="text-foreground">Integrations → Webhooks</strong>)</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                <span>Click <strong className="text-foreground">Create Webhook</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                <span>Paste the <strong className="text-foreground">Webhook URL</strong> above. Select events: <code className="text-xs bg-muted px-1 rounded">deployment.error</code>, <code className="text-xs bg-muted px-1 rounded">deployment.succeeded</code>, <code className="text-xs bg-muted px-1 rounded">deployment.created</code>, <code className="text-xs bg-muted px-1 rounded">deployment.checks.failed</code>, <code className="text-xs bg-muted px-1 rounded">deployment.rollback</code>, <code className="text-xs bg-muted px-1 rounded">alerts.triggered</code>, <code className="text-xs bg-muted px-1 rounded">firewall.attack</code>, <code className="text-xs bg-muted px-1 rounded">project.domain.unverified</code></span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                <span>Leave <strong className="text-foreground">Projects</strong> as "All Projects" or select specific ones</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">5</span>
                <span>Click <strong className="text-foreground">Create</strong>. Vercel will show a <strong className="text-foreground">Webhook Signing Secret</strong> — copy it and paste it below.</span>
              </li>
            </ol>

            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Webhook Signing Secret</p>
                {webhookSecretConfigured && (
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Configured</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Vercel signs every webhook with HMAC SHA1 using this secret. When configured, Kestrel verifies the <code className="bg-background px-1 rounded">x-vercel-signature</code> header to reject forged requests.</p>
              <div className="flex items-center gap-2">
                <Input
                  type="password"
                  value={webhookSecret}
                  onChange={e => setWebhookSecret(e.target.value)}
                  placeholder={webhookSecretConfigured ? '••••••••••••••••' : 'Paste secret from Vercel'}
                  className="flex-1"
                />
                <Button size="sm" onClick={handleSaveWebhookSecret} disabled={savingSecret || !webhookSecret}>
                  {savingSecret ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {webhookSecretConfigured ? 'Update' : 'Save'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Vercel actions and triggers in your workflows to automate deployment monitoring and incident response.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Deployment Failed', desc: 'Triggers when a Vercel deployment encounters a build or runtime error.', Icon: XCircle },
                  { name: 'Deployment Succeeded', desc: 'Triggers when a deployment successfully completes and is live.', Icon: CheckCircle2 },
                  { name: 'Deployment Checks Failed', desc: 'Triggers when post-deployment checks (E2E tests, Lighthouse) fail.', Icon: XOctagon },
                  { name: 'Production Rollback', desc: 'Triggers when a production deployment is rolled back.', Icon: RotateCcw },
                  { name: 'Firewall Attack Detected', desc: 'Triggers when Vercel WAF detects a firewall attack.', Icon: ShieldAlert },
                  { name: 'Error Anomaly', desc: 'Triggers when Vercel alerts detect an anomalous error spike.', Icon: AlertTriangle },
                  { name: 'Domain Issue', desc: 'Triggers when a project domain becomes unverified or has DNS issues.', Icon: Globe },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Get Deployment', desc: 'Retrieve details and status of a specific deployment by ID or URL.', Icon: Rocket },
                  { name: 'Get Build Logs', desc: 'Fetch build output logs for a deployment to diagnose failures.', Icon: FileText },
                  { name: 'Rollback Production', desc: 'Instantly roll back production to the previous deployment.', Icon: RotateCcw },
                  { name: 'Promote to Production', desc: 'Promote a preview deployment to production for a project.', Icon: ArrowUpCircle },
                  { name: 'List Deployments', desc: 'List recent deployments for a project, filtered by branch or status.', Icon: List },
                  { name: 'Investigate Vercel', desc: 'Investigate build failures, configuration issues, deployment problems, and more.', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a Vercel deployment fails on the{' '}
              <input
                value={exampleBranch}
                onChange={e => setExampleBranch(e.target.value)}
                placeholder="branch"
                style={{ width: `${Math.max(exampleBranch.length || 6, 6)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              branch, get the build logs, DM the{' '}
              <input
                value={exampleDeveloper}
                onChange={e => setExampleDeveloper(e.target.value)}
                placeholder="developer"
                style={{ width: `${Math.max(exampleDeveloper.length || 9, 9)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              on Slack with the error summary, and create a Jira bug ticket.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleBranch || !exampleDeveloper}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a Vercel deployment fails on the ${exampleBranch} branch, get the build logs, DM the ${exampleDeveloper} on Slack with the error summary, and create a Jira bug ticket.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={vcIcon} alt="Vercel" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>Connect Vercel</CardTitle>
                <CardDescription>Connect your Vercel account to enable deployment monitoring, build log analysis, and rollback automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="vc-token" className="text-sm">API Token</Label>
              <Input
                id="vc-token"
                type="password"
                value={apiToken}
                onChange={e => setApiToken(e.target.value)}
                placeholder="Your Vercel API token"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Create one at Vercel → Account Settings (click your avatar) → Tokens. Scope the token to your team for team-level access.</p>
            </div>
            <div>
              <Label htmlFor="vc-team" className="text-sm">Team ID <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="vc-team"
                value={teamId}
                onChange={e => setTeamId(e.target.value)}
                placeholder="team_..."
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Found in Vercel → Settings → General. Leave empty for personal accounts.</p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !apiToken}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Vercel
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VercelIntegration;
